import React, { useState, useEffect, useCallback } from 'react';
import { LoadScript } from '@react-google-maps/api';
import DeviceStatus from './components/DeviceStatus';
import DeviceMap from './components/DeviceMap';
import SensorGauges from './components/SensorGauges';
import SettingsPanel from './components/SettingsPanel';
import { fetchDashboard, updateSettings, sendSummaryNow } from './services/api';
import { DashboardData, SettingsUpdate, SensorData } from './types';
import { useSensorSSE } from './hooks/useSensorSSE';
import './App.css';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

const App: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<'dashboard' | 'settings'>('dashboard');

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchDashboard();
      setDashboardData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard');
      console.error('Error loading dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  // Handle real-time sensor updates via SSE
  const handleSensorUpdate = useCallback((updateData: any) => {
    if (!dashboardData) return;

    console.log('[App] Received sensor update:', updateData);

    // Update sensor data in state
    setDashboardData((prev) => {
      if (!prev) return prev;

      const updatedSensorData: SensorData[] = [];

      // Map incoming sensor data to SensorData format
      if (updateData.sensors.temperature) {
        updatedSensorData.push({
          sensor_type: 'temperature',
          value: updateData.sensors.temperature.value,
          unit: updateData.sensors.temperature.unit,
          timestamp: updateData.timestamp,
          is_simulated: false,
        });
      }

      if (updateData.sensors.humidity) {
        updatedSensorData.push({
          sensor_type: 'humidity',
          value: updateData.sensors.humidity.value,
          unit: updateData.sensors.humidity.unit,
          timestamp: updateData.timestamp,
          is_simulated: false,
        });
      }

      if (updateData.sensors.air_quality) {
        updatedSensorData.push({
          sensor_type: 'air_quality',
          value: updateData.sensors.air_quality.value,
          unit: updateData.sensors.air_quality.unit,
          timestamp: updateData.timestamp,
          is_simulated: false,
        });
      }

      return {
        ...prev,
        sensor_data: updatedSensorData,
      };
    });
  }, [dashboardData]);

  // Initialize SSE connection for real-time updates
  const { status: sseStatus } = useSensorSSE(
    handleSensorUpdate,
    dashboardData?.device.device_id
  );

  const handleSaveSettings = async (updates: SettingsUpdate) => {
    if (!dashboardData) return;

    await updateSettings(dashboardData.device.device_id, updates);
    await loadDashboard(); // Reload to get updated settings
  };

  const handleSendSummary = async () => {
    if (!dashboardData) return;

    await sendSummaryNow(dashboardData.device.device_id);
  };

  if (loading && !dashboardData) {
    return (
      <div className="app-loading">
        <div className="loader"></div>
        <p>Loading Inkspire...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app-error">
        <h2>⚠️ Error Loading Dashboard</h2>
        <p>{error}</p>
        <button onClick={loadDashboard}>Retry</button>
        <div className="error-details">
          <p>Make sure the backend API is running:</p>
          <code>python application.py</code>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return null;
  }

  return (
    <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
      <div className="app">
      <header className="app-header">
        <div className="header-content">
          <div className="logo">
            <svg className="logo-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L3 7V17L12 22L21 17V7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 22V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 12L3 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 12L21 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <h1>Inkspire</h1>
          </div>
          <nav className="nav-buttons">
            <button
              className={`nav-btn ${activeView === 'dashboard' ? 'active' : ''}`}
              onClick={() => setActiveView('dashboard')}
            >
              Dashboard
            </button>
            <button
              className={`nav-btn ${activeView === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveView('settings')}
            >
              Settings
            </button>
          </nav>
        </div>
      </header>

      <main className="app-main">
        <div className="container">
          {activeView === 'dashboard' && (
            <>
              <DeviceStatus device={dashboardData.device} />

              <div className="grid-2-col">
                <DeviceMap
                  location={dashboardData.latest_location}
                  deviceName={dashboardData.device.device_name}
                />
                <SensorGauges sensorData={dashboardData.sensor_data} />
              </div>
            </>
          )}

          {activeView === 'settings' && (
            <SettingsPanel
              settings={dashboardData.settings}
              onSave={handleSaveSettings}
              onSendSummary={handleSendSummary}
            />
          )}
        </div>
      </main>

      <footer className="app-footer">
        <p>
          Inkspire • Organize your day, simplify your life
          {loading && <span className="sync-indicator"> • Syncing...</span>}
          {sseStatus.connected && (
            <span className="connection-indicator connected"> • Live</span>
          )}
          {sseStatus.reconnecting && (
            <span className="connection-indicator reconnecting"> • Reconnecting...</span>
          )}
          {sseStatus.error && !sseStatus.reconnecting && (
            <span className="connection-indicator error"> • Offline</span>
          )}
        </p>
      </footer>
    </div>
    </LoadScript>
  );
};

export default App;
